import { useCallback } from 'react';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { copyElementToClipboard } from '@backrs_ui/grow-ui/src/components/clipboard/copyElementToClipboard';
import { sessionNames } from '@backrs_ui/grow-ui/src/components/SolutionSession/SessionStrings';
import { useGetSessionToolTitle } from '@backrs_ui/grow-ui/src/hooks/useSessionToolTitle';
import { SUPERSESSION_TITLES } from '@backrs_ui/grow-ui/src/models/models';
import { usePostBookmarkMutation } from '@backrs_ui/grow-ui/src/redux/bookmarks/bookmarks.api';
import { useUpdateResponseFeedbackMutation } from '@backrs_ui/grow-ui/src/redux/coachLubav/coachLubav.api';
import { useCreateGeneratedContentMutation } from '@backrs_ui/grow-ui/src/redux/generatedContent/generatedContent.api';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { selectUserId } from '@backrs_ui/grow-ui/src/redux/user/user';

const { chatBox } = appStrings.modules;

type UseMessageActionsProps = Readonly<{
  activeSession: string | null;
}>;

export const useMessageActions = ({ activeSession }: UseMessageActionsProps) => {
  const userId = useAppSelector(selectUserId);
  const [updateResponseFeedbackMutation] = useUpdateResponseFeedbackMutation();
  const [createGeneratedContentMutation] = useCreateGeneratedContentMutation();
  const [postBookmarkMutation] = usePostBookmarkMutation();
  const getSessionToolTitle = useGetSessionToolTitle();

  const getSessionDisplayName = useCallback(
    (sessionType: string) =>
      getSessionToolTitle(sessionType) ??
      SUPERSESSION_TITLES[sessionType] ??
      (sessionNames as Record<string, string>)[sessionType] ??
      sessionType,
    [getSessionToolTitle],
  );

  const handleCopy = useCallback(async (text: string, element?: HTMLElement | null) => {
    if (element && (await copyElementToClipboard(element))) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error(chatBox.copyErr, err);
    }
  }, []);

  const handleThumbsUp = useCallback(
    async (id: string) => {
      try {
        const responseId = parseInt(id);

        if (isNaN(responseId)) {
          return;
        }

        await updateResponseFeedbackMutation({
          response_id: responseId,
          liked_response: true,
        });
      } catch (error) {
        console.error('Error updating thumbs up feedback:', error);
      }
    },
    [updateResponseFeedbackMutation],
  );

  const handleThumbsDown = useCallback(
    async (id: string) => {
      try {
        const responseId = parseInt(id);

        if (isNaN(responseId)) {
          return;
        }

        await updateResponseFeedbackMutation({
          response_id: responseId,
          liked_response: false,
        });
      } catch (error) {
        console.error('Error updating thumbs down feedback:', error);
      }
    },
    [updateResponseFeedbackMutation],
  );

  const handleBookmark = useCallback(
    async (responseText: string) => {
      try {
        if (!userId || !activeSession) {
          console.error('Missing user ID or active session for bookmarking');

          return;
        }

        const sessionDisplayName = getSessionDisplayName(activeSession);

        const now = new Date();
        const title = `Lubav Message - ${sessionDisplayName} - ${now.toLocaleDateString()}`;

        const generatedContent = await createGeneratedContentMutation({
          user_id: userId,
          major_source: 'Ask Lubav',
          minor_source: sessionDisplayName,
          title: title,
          content: responseText,

          // we'll just use a truncated version for the summary
          summary: responseText.substring(0, 200) + (responseText.length > 200 ? '...' : ''),
        }).unwrap();

        await postBookmarkMutation({
          itemId: generatedContent.id || 0,
          type: 'generated-content',
        });
      } catch (error) {
        console.error('Error bookmarking response:', error);
      }
    },
    [
      userId,
      activeSession,
      getSessionDisplayName,
      createGeneratedContentMutation,
      postBookmarkMutation,
    ],
  );

  return {
    handleCopy,
    handleThumbsUp,
    handleThumbsDown,
    handleBookmark,
  };
};
